import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, FileText, Check, X, RefreshCw, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentSettings = () => {
  const token = localStorage.getItem('token');
  
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({
    document_name: '',
    is_mandatory: true,
    allowed_formats: ['pdf', 'jpg', 'jpeg', 'png'],
    max_size_kb: 100, // Default 100KB, will be capped based on document type
    description: ''
  });

  // Copy modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [targetCourse, setTargetCourse] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchDocuments();
    }
  }, [selectedCourse]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const coursesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setCourses(coursesData);
      if (coursesData.length > 0) {
        setSelectedCourse(coursesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    console.log('📄 Fetching documents for course:', selectedCourse);
    try {
      // include_defaults=true shows default documents when none are configured
      // Note: trailing slash is important to avoid redirect issues
      const url = `${API_URL}/api/document-settings/?course_id=${selectedCourse}&include_defaults=true`;
      console.log('📄 API URL:', url);
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📄 Documents received:', response.data?.length, response.data);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load document settings');
    } finally {
      setLoading(false);
    }
  };

  // System-level max size limits
  const MAX_SIZE_PHOTO_SIGNATURE = 70; // KB - for photo/signature documents
  const MAX_SIZE_OTHER_DOCUMENTS = 300; // KB - for other documents
  
  // Check if document is photo/signature type
  const isPhotoSignatureType = (docName) => {
    const name = (docName || '').toLowerCase();
    return name.includes('photo') || name.includes('signature') || name.includes('passport');
  };
  
  // Get max allowed size based on document type
  const getMaxAllowedSize = (docName) => {
    return isPhotoSignatureType(docName) ? MAX_SIZE_PHOTO_SIGNATURE : MAX_SIZE_OTHER_DOCUMENTS;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.document_name.trim()) {
      toast.error('Document name is required');
      return;
    }
    
    // Validate max_size_kb based on document type
    const maxAllowed = getMaxAllowedSize(formData.document_name);
    if (formData.max_size_kb > maxAllowed) {
      const docType = isPhotoSignatureType(formData.document_name) ? 'Photo/Signature' : 'Document';
      toast.error(`${docType} max size cannot exceed ${maxAllowed}KB`);
      return;
    }

    try {
      if (editingDoc) {
        // Update
        await axios.put(`${API_URL}/api/document-settings/${editingDoc.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Document setting updated');
      } else {
        // Create
        await axios.post(`${API_URL}/api/document-settings/`, {
          ...formData,
          course_id: selectedCourse
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Document setting created');
      }
      
      setShowModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error(error.response?.data?.detail || 'Failed to save document setting');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.document_name}"?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/document-settings/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document setting deleted');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setFormData({
      document_name: doc.document_name,
      is_mandatory: doc.is_mandatory,
      allowed_formats: doc.allowed_formats,
      max_size_kb: doc.max_size_kb,
      description: doc.description || ''
    });
    setShowModal(true);
  };

  const handleCreateDefaults = async () => {
    if (!window.confirm('Create default document settings for this course?')) return;
    
    try {
      await axios.post(`${API_URL}/api/document-settings/create-defaults/${selectedCourse}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default documents created');
      fetchDocuments();
    } catch (error) {
      console.error('Error creating defaults:', error);
      toast.error(error.response?.data?.detail || 'Failed to create defaults');
    }
  };

  const handleCopyToCourse = async () => {
    if (!targetCourse) {
      toast.error('Select target course');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/document-settings/copy-to-course?source_course_id=${selectedCourse}&target_course_id=${targetCourse}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documents copied to target course');
      setShowCopyModal(false);
      setTargetCourse('');
    } catch (error) {
      console.error('Error copying:', error);
      toast.error(error.response?.data?.detail || 'Failed to copy');
    }
  };

  const resetForm = () => {
    setEditingDoc(null);
    setFormData({
      document_name: '',
      is_mandatory: true,
      allowed_formats: ['pdf', 'jpg', 'jpeg', 'png'],
      max_size_kb: 100, // Default 100KB
      description: ''
    });
  };

  const toggleFormat = (format) => {
    setFormData(prev => ({
      ...prev,
      allowed_formats: prev.allowed_formats.includes(format)
        ? prev.allowed_formats.filter(f => f !== format)
        : [...prev.allowed_formats, format]
    }));
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || 'Unknown';
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Settings</h1>
          <p className="text-sm text-gray-500">Configure required documents for each course</p>
        </div>
      </div>

      {/* Course Selector & Actions */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Select Course:</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            >
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
            {documents.length === 0 && (
              <button
                onClick={handleCreateDefaults}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Settings className="h-4 w-4" />
                Create Defaults
              </button>
            )}
            <button
              onClick={() => setShowCopyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
              disabled={documents.length === 0}
            >
              <Copy className="h-4 w-4" />
              Copy to Course
            </button>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Document
            </button>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-300" />
            <p className="mt-2 text-gray-500">No document settings for this course</p>
            <button
              onClick={handleCreateDefaults}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Default Documents
            </button>
          </div>
        ) : (
          <>
            {/* Show info banner if showing defaults */}
            {documents.some(d => d.is_default) && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between">
                <p className="text-sm text-amber-800">
                  📋 <strong>Default Documents:</strong> No documents configured for this course yet. Showing default documents below.
                </p>
                <button
                  onClick={handleCreateDefaults}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                >
                  Save as Course Documents
                </button>
              </div>
            )}
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allowed Formats</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max Size</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc, index) => (
                <tr key={doc.id} className={`hover:bg-gray-50 ${doc.is_default ? 'bg-amber-50/50' : ''}`}>
                  <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-900">{doc.document_name}</span>
                      {doc.is_default && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Default</span>
                      )}
                    </div>
                    {doc.description && (
                      <p className="text-xs text-gray-500 mt-1">{doc.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {doc.is_mandatory ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <Check className="h-3 w-3 mr-1" /> Required
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Optional
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {doc.allowed_formats?.map(format => (
                        <span key={format} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                          .{format.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {doc.max_size_kb} KB
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {!doc.is_default ? (
                        <>
                          <button
                            onClick={() => handleEdit(doc)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(doc)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">Default</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingDoc ? 'Edit Document Setting' : 'Add Document Setting'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Name *
                  </label>
                  <input
                    type="text"
                    value={formData.document_name}
                    onChange={(e) => setFormData({...formData, document_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 10th Marksheet"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_mandatory"
                    checked={formData.is_mandatory}
                    onChange={(e) => setFormData({...formData, is_mandatory: e.target.checked})}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="is_mandatory" className="text-sm text-gray-700">
                    Mandatory (Required for form submission)
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allowed Formats
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['pdf', 'jpg', 'jpeg', 'png'].map(format => (
                      <button
                        key={format}
                        type="button"
                        onClick={() => toggleFormat(format)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          formData.allowed_formats.includes(format)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        .{format.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max File Size (KB)
                  </label>
                  <input
                    type="number"
                    value={formData.max_size_kb}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 50;
                      const maxAllowed = getMaxAllowedSize(formData.document_name);
                      setFormData({...formData, max_size_kb: Math.min(value, maxAllowed)});
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="10"
                    max={getMaxAllowedSize(formData.document_name)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isPhotoSignatureType(formData.document_name) 
                      ? `Photo/Signature: Max ${MAX_SIZE_PHOTO_SIGNATURE}KB allowed` 
                      : `Documents: Max ${MAX_SIZE_OTHER_DOCUMENTS}KB allowed`}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Upload scanned copy"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingDoc ? 'Update' : 'Add Document'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Copy to Course Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Copy Documents to Another Course
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From: <span className="text-blue-600">{getCourseName(selectedCourse)}</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To Course:
                  </label>
                  <select
                    value={targetCourse}
                    onChange={(e) => setTargetCourse(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select target course</option>
                    {courses.filter(c => c.id !== selectedCourse).map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => { setShowCopyModal(false); setTargetCourse(''); }}
                    className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCopyToCourse}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    disabled={!targetCourse}
                  >
                    Copy Documents
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSettings;
